import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppActions } from '../../../state/app.actions';
import { LyaService } from '../services/lya.service';
import { LyaActions } from './lya.actions';
import { INITIAL_LYA_STATE, LYA_STATE_TOKEN, LyaStateModel } from './lya.models';
import {
  CurrentQuestion,
  LyaAutoInterviewCandidateMeta,
  LyaQuestion,
  QuestionResponse,
} from './lya.types';

@State<LyaStateModel>({
  name: LYA_STATE_TOKEN,
  defaults: INITIAL_LYA_STATE,
})
@Injectable()
export class LyaState {
  lyaService = inject(LyaService);

  @Action(LyaActions.getCandidateInterviewMeta)
  getConversationMeta(
    ctx: StateContext<LyaStateModel>,
    action: InstanceType<typeof LyaActions.getCandidateInterviewMeta>,
  ) {
    ctx.dispatch(new AppActions.showLoader());
    if (!!action.companyId) {
      ctx.patchState({ companyId: action.companyId });
    }
    ctx.patchState({ interviewKey: action.id });
    return this.lyaService.getCandidateInterviewMeta(action.id, action.companyId).pipe(
      tap((conversationMeta) => {
        //TODO: check cv file too to set generating state
        const isGenerating =
          conversationMeta?.questions?.length === 0 && !!conversationMeta.cvPresent;
        ctx.dispatch(new AppActions.hideLoader());
        ctx.patchState({ conversationMeta, generatingQuestions: isGenerating });
        ctx.dispatch(new LyaActions.cleanQuestions());
        const companyId = conversationMeta?.companyId ?? action.companyId;
        if (companyId) {
          ctx.dispatch(new LyaActions.getCompanyLogo(companyId));
        }
      }),
      catchError((error) => {
        ctx.dispatch(new AppActions.hideLoader());
        ctx.dispatch(new AppActions.toastError(error));
        return of(error);
      }),
    );
  }

  @Action(LyaActions.getCompanyLogo)
  getCompanyLogo(
    ctx: StateContext<LyaStateModel>,
    action: InstanceType<typeof LyaActions.getCompanyLogo>,
  ) {
    return this.lyaService.getCompanyPublicData(action.companyId).pipe(
      tap((info) => {
        ctx.patchState({ companyLogoUrl: info?.logoUrl ?? null });
      }),
      catchError(() => {
        ctx.patchState({ companyLogoUrl: null });
        return of(null);
      }),
    );
  }

  @Action(LyaActions.sendCV)
  sendCV(ctx: StateContext<LyaStateModel>, action: InstanceType<typeof LyaActions.sendCV>) {
    ctx.dispatch(new AppActions.showLoader());
    const state = ctx.getState();
    return this.lyaService
      .attachCV(state.companyId ?? '', state.interviewKey ?? '', action.file)
      .pipe(
        tap((conversationMeta) => {
          ctx.dispatch(new AppActions.hideLoader());
          ctx.patchState({ generatingQuestions: true });
        }),
        catchError((error) => {
          ctx.dispatch(new AppActions.hideLoader());
          ctx.dispatch(new AppActions.toastError(error));
          return error;
        }),
      );
  }

  @Action(LyaActions.setConversationQuestionsMeta)
  setConversationQuestionsMeta(
    ctx: StateContext<LyaStateModel>,
    action: InstanceType<typeof LyaActions.setConversationQuestionsMeta>,
  ) {
    ctx.patchState({
      conversationMeta: action.data,
      generatingQuestions: false,
    });
    ctx.dispatch(new LyaActions.cleanQuestions());
  }

  @Action(LyaActions.cleanQuestions)
  cleanQuestions(ctx: StateContext<LyaStateModel>) {
    const data = ctx.getState().conversationMeta;
    const { toAsk, responded, totalQuestions } = (data?.questions ?? []).reduce(
      (acc, category, categoryIndex) => {
        acc.totalQuestions += category.questions.length;
        category.questions.forEach((question, index) => {
          const questionItem: LyaQuestion = {
            ...question,
            category: category.category,
            questionIndex: index,
            categoryIndex,
          };
          if (question.responded) {
            acc.responded.push(questionItem);
          } else {
            acc.toAsk.push(questionItem);
          }
        });
        return acc;
      },
      { toAsk: [] as LyaQuestion[], responded: [] as LyaQuestion[], totalQuestions: 0 },
    );

    ctx.patchState({
      questionsForAsk: toAsk,
      respondedQuestions: responded,
      totalQuestions,
      endInterview: toAsk.length === 0,
    });
  }

  @Action(LyaActions.nextQuestion)
  nextQuestion(
    ctx: StateContext<LyaStateModel>,
    action: InstanceType<typeof LyaActions.nextQuestion>,
  ) {
    const conversationMeta = ctx.getState().conversationMeta;
    let questionsForAsk = [...ctx.getState().questionsForAsk];
    const currentQuestion = ctx.getState().currentQuestion;
    const futureQuestion = questionsForAsk?.shift();
    questionsForAsk = [...questionsForAsk];
    if (currentQuestion) {
      return this.answerQuestion(ctx, futureQuestion, questionsForAsk, conversationMeta);
    }

    this.updateNextQuestion(ctx, futureQuestion, questionsForAsk, conversationMeta);
    return;
  }

  @Action(LyaActions.terminateConversation)
  terminateConversation(ctx: StateContext<LyaStateModel>) {}

  updateNextQuestion(
    ctx: StateContext<LyaStateModel>,
    futureQuestion: LyaQuestion | undefined,
    questionsForAsk: LyaQuestion[],
    conversationMeta: LyaAutoInterviewCandidateMeta | null,
  ) {
    if (!futureQuestion) {
      ctx.dispatch(new LyaActions.terminateConversation());
      ctx.patchState({ endInterview: true, currentQuestion: null });
      return;
    }
    /*
    let previousResponseSize = 0;

    if (ctx.getState().currentQuestion) {
      previousResponseSize =
        (ctx.getState().currentQuestion?.response?.length ?? 0) +
        (ctx.getState().currentQuestion?.previousResponseSize ?? 0);

      console.log('SET SIZE OF PREVIOUS RESPONSE', previousResponseSize, '==>');
    }*/
    const audioUrl = `${environment.apiUrl}/hub/files/download?path=${conversationMeta?.companyId}/lya_auto/${conversationMeta?.id}/${futureQuestion.audioFileName}`;
    ctx.patchState({
      currentQuestion: {
        question: futureQuestion,
        response: '',
        audioUrl,
        index: ctx.getState().totalQuestions - questionsForAsk.length,
        //previousResponseSize,
      },
      questionsForAsk,
    });
  }

  answerQuestion(
    ctx: StateContext<LyaStateModel>,
    futureQuestion: LyaQuestion | undefined,
    questionsForAsk: LyaQuestion[],
    conversationMeta: LyaAutoInterviewCandidateMeta | null,
  ) {
    const currentQuestion = ctx.getState().currentQuestion;
    if (!currentQuestion) return;
    const cutIndex =
      ctx
        .getState()
        .responsesSize.filter((r) => r.id !== currentQuestion.question.id)
        .sort((a, b) => b.size - a.size)
        .shift()?.size ?? 0;

    const responseValue = currentQuestion.response?.slice(cutIndex) ?? '';
    const response: QuestionResponse = {
      id: currentQuestion.question.id,
      category: currentQuestion.question.category ?? '',
      question: currentQuestion.question.question,
      response: responseValue,
      conversationId: conversationMeta?.id ?? '',
      categoryIndex: currentQuestion.question.categoryIndex ?? 0,
      questionIndex: currentQuestion.question.questionIndex ?? 0,
      companyId: conversationMeta?.companyId ?? '',
    };

    return this.lyaService.questionResponse(response.companyId, response).pipe(
      tap(() => {
        this.updateNextQuestion(ctx, futureQuestion, questionsForAsk, conversationMeta);
      }),
    );
  }
  @Action(LyaActions.setTranscriptionResponse)
  setTranscriptionResponse(
    ctx: StateContext<LyaStateModel>,
    action: InstanceType<typeof LyaActions.setTranscriptionResponse>,
  ) {
    if (!ctx.getState().currentQuestion) return;
    const questionId = ctx.getState().currentQuestion?.question.id;
    if (questionId === null || questionId === undefined) return;
    const responsesSize = ctx.getState().responsesSize.map((r) => {
      return r.id === questionId ? { ...r, size: action.data.length } : r;
    });
    if (!responsesSize.find((r) => r.id === questionId)) {
      responsesSize.push({ id: questionId, size: action.data.length });
    }
    ctx.patchState({
      responsesSize,
      currentQuestion: {
        ...ctx.getState().currentQuestion,
        response: action.data,
      } as CurrentQuestion,
    });
  }
}
